import {
  type MaritalStatus,
  useGetEnrollmentConfirmInformationV2Data,
  useSubmitEnrollmentConfirmInformationV2Data,
} from '@egw/api'
import {
  type EmploymentStatus,
  type EnrollmentInvestmentExperienceCategory,
  type EnrollmentInvestmentObjectiveCategory,
  type EnrollmentTaxFilingStatusType,
  type Occupation,
  type PersonalNetWorthRange,
} from '@egw/api/src/models/enrollment/enrollment'
import { Alert, AlertType, Button, GenericError } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { EnrollmentV2Layout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useRouter } from '@egw/router'
import { formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { type FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { PageHeader } from '../../../../components'
import {
  EditAnnualIncome,
  EditBrokerDealer,
  EditEmploymentOccupation,
  EditExecShareholder,
  EditInvestmentExperience,
  EditInvestmentObjective,
  EditMaritalStatus,
  EditNetWorth,
  EditTaxFiling,
} from '../../../../components/editFields'
import { EditPoliticallyExposedPerson } from '../../../../components/editFields/edit-politically-exposed-person/edit-politically-exposed-person'
import { ALL_ENROLLMENT_ROUTES } from '../../../../constants'
import { useValidateRoute } from '../../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - ConfirmInformationV2')

interface FormData {
  employmentStatus: EmploymentStatus
  occupation: Occupation
  isBrokerDealer: boolean | number
  isDirectorPolicyMakerOfCompany: boolean | number // Executive or sharedholder
  directorPolicyMakerOfCompany: string
  isPoliticallyExposedPerson: boolean | number
  affiliatedPoliticalOrganizations: string[] | string | undefined
  hasPoliticallyExposedImmediateFamilyMembers: boolean | number
  politicallyExposedImmediateFamilyMembers: string[] | string | undefined
  maritalStatus: MaritalStatus | string | undefined
  objective: string
  experience: string
  taxFilingStatus: string
  income: number | string | undefined
  netWorth: string
}

export const ConfirmInformationV2Page: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentConfirmInformationV2Data()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentConfirmInformationV2Data()

  const methods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      employmentStatus: undefined as EmploymentStatus | undefined,
      occupation: undefined as Occupation | undefined,
      isBrokerDealer: undefined as boolean | number | undefined,
      isDirectorPolicyMakerOfCompany: undefined as boolean | undefined,
      directorPolicyMakerOfCompany: '',
      isPoliticallyExposedPerson: undefined as boolean | undefined,
      affiliatedPoliticalOrganizations: [],
      hasPoliticallyExposedImmediateFamilyMembers: undefined as boolean | undefined,
      politicallyExposedImmediateFamilyMembers: [],
      maritalStatus: undefined as MaritalStatus | undefined,
      taxFilingStatus: undefined as EnrollmentTaxFilingStatusType | undefined,
      income: '', // Leave as empty string to avoid validation error
      netWorth: undefined as PersonalNetWorthRange | undefined,
      objective: undefined as EnrollmentInvestmentObjectiveCategory | undefined,
      experience: undefined as EnrollmentInvestmentExperienceCategory | undefined,
    },
  })

  useEffect(() => {
    if (data && data.__typename === 'SubmitEnrollmentSuccess') {
      methods.reset({
        employmentStatus: data.enrollmentData?.userInfo?.employmentStatus,
        occupation: data.enrollmentData?.userInfo?.occupation,
        isBrokerDealer: Number(data.enrollmentData?.userInfo?.investment?.isBrokerDealer), // Convert to number to avoid validation error w/ Radio Input
        isDirectorPolicyMakerOfCompany: Number(
          data.enrollmentData?.userInfo?.investment?.isDirectorPolicyMakerOfCompany,
        ), // Convert to number to avoid validation error w/ Radio Input
        directorPolicyMakerOfCompany: data.enrollmentData?.userInfo?.investment?.directorPolicyMakerOfCompanyName,
        isPoliticallyExposedPerson: Number(data.enrollmentData?.userInfo?.investment?.isPoliticallyExposedPerson), // Convert to number to avoid validation error w/ Radio Input
        affiliatedPoliticalOrganizations:
          data.enrollmentData?.userInfo?.investment?.affiliatedPoliticalOrganizations?.join(', '),
        hasPoliticallyExposedImmediateFamilyMembers: Number(
          data.enrollmentData?.userInfo?.investment?.hasPoliticallyExposedImmediateFamilyMembers,
        ),
        politicallyExposedImmediateFamilyMembers:
          data.enrollmentData?.userInfo?.investment?.politicallyExposedImmediateFamilyMembers?.join(', '),
        maritalStatus: data.enrollmentData?.userInfo?.maritalStatus,
        taxFilingStatus: data.enrollmentData?.userInfo?.taxFilingStatus,
        income: formatInputToCurrency(data.enrollmentData?.userInfo?.income?.toString() ?? '', true),
        netWorth: data.enrollmentData?.userInfo?.netWorthRange,
        objective: data.enrollmentData?.userInfo?.investment?.objective,
        experience: data.enrollmentData?.userInfo?.investment?.experience,
      })
    }
  }, [data, methods])

  // Protect route
  useValidateRoute(data?.path)

  // ON SUBMIT CONFIRMS INFORMATION, AND ONSUCCESS PROCEEEDS TO NEXT SCREEN
  const onSubmit = async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    const payload = {
      employmentStatus: formData.employmentStatus,
      occupation: formData.occupation === ('' as Occupation) ? undefined : formData.occupation,
      isBrokerDealer: Boolean(formData.isBrokerDealer),
      isDirectorPolicyMakerOfCompany: Boolean(formData.isDirectorPolicyMakerOfCompany),
      directorPolicyMakerOfCompany: formData.directorPolicyMakerOfCompany,
      investmentProfile: {
        isPoliticallyExposedPerson: Boolean(formData.isPoliticallyExposedPerson),
        affiliatedPoliticalOrganizations: formData.affiliatedPoliticalOrganizations
          ? (formData.affiliatedPoliticalOrganizations as string).split(',').map((e) => e.trimStart())
          : [], // If empty string pass an empty array otherwise split the string by comma
        hasPoliticallyExposedImmediateFamilyMembers: Boolean(formData.hasPoliticallyExposedImmediateFamilyMembers),
        politicallyExposedImmediateFamilyMembers: formData.politicallyExposedImmediateFamilyMembers
          ? (formData.politicallyExposedImmediateFamilyMembers as string).split(',').map((e) => e.trimStart())
          : [], // If empty string pass an empty array otherwise split the string by comma
        taxFilingStatus: formData.taxFilingStatus as EnrollmentTaxFilingStatusType,
        objective: formData.objective as EnrollmentInvestmentObjectiveCategory,
        experience: formData.experience as EnrollmentInvestmentExperienceCategory,
        maritalStatus: formData.maritalStatus as MaritalStatus,
      },
      personalAnnualGrossIncome: removeCurrencySymbols(formData.income as string),
      personalNetWorthRange: formData.netWorth as PersonalNetWorthRange,
    }

    try {
      const confirmInfoResponse = await mutate({
        ...payload,
      })

      if (
        confirmInfoResponse?.__typename === 'SubmitEnrollmentSuccess' &&
        confirmInfoResponse.missingField === 'proceedConfirmInfo'
      ) {
        const proceedResponse = await mutate({
          proceedConfirmInfo: true,
        })

        if (proceedResponse?.path) {
          // This path should take the user to Disclosures page
          navigate(proceedResponse.path)
        } else {
          // GO to error page
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
        }
      }
      // TODO ELSE HANDLER NEEDS TO BE ADDED FOR ERROR TYPE
    } catch (e) {
      log.error('Error submitting email', e)
    }
  }

  const closeHandler = (): void => {
    try {
      void mutate({
        abandonApplication: true,
      })
      navigate(WEALTH_ROUTES.INVESTMENTS.path)
    } catch (e) {
      log.error('Error abandoning application', e)
    }
  }

  return (
    <EnrollmentV2Layout closeHandler={closeHandler}>
      <div className="h-full flex flex-col w-full">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className="flex-1">
              <PageHeader title={t('investments_v2.confirm_information.title')} />

              <p className="typo-primary-sm-regular text-text-meta mb-2xl">
                {t('investments_v2.confirm_information.description')}
              </p>

              {/* EMPLOYMENT INFORMATION */}
              <p className="typo-primary-lg-semibold text-text-header mb-2xs">
                {t('investments_v2.confirm_information.employment')}
              </p>
              <EditEmploymentOccupation />
              <EditBrokerDealer />
              <EditExecShareholder />
              <EditPoliticallyExposedPerson />
              {/* FINANCIAL INFORMATION */}
              <p className="typo-primary-lg-semibold text-text-header mt-lg mb-2xs">
                {t('investments_v2.confirm_information.finances')}
              </p>
              <EditTaxFiling />
              <EditMaritalStatus />
              <EditAnnualIncome />
              <EditNetWorth />
              <EditInvestmentObjective />
              <EditInvestmentExperience />

              <div className="flex-1 md:hidden" />

              {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

              {/* AUTO GENERATED COMPONENTS */}

              <Button
                size="small"
                type="submit"
                className="w-full md:w-min mt-lg"
                isDisabled={!methods.formState.isValid}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
              >
                {t('investments_v2.confirm_information.cta')}
              </Button>
            </form>
          </FormProvider>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentV2Layout>
  )
}
